import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Clock, ChevronDown, Trash2, CalendarClock, Pencil } from 'lucide-react';

// Home dropdown showing the late checkouts scheduled for the selected day.
// Grey when empty, red→orange gradient counter when there are any (for this day + this user).
export default function LateCheckoutDropdown({ items = [], canDelete = false, canEdit = false, onDelete, onEdit, testid = 'late-checkout-dropdown' }) {
  const count = items.length;
  const active = count > 0;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className={`inline-flex items-center gap-2 h-12 px-3.5 rounded-2xl border bg-white text-sm font-semibold shadow-sm transition-colors w-full justify-between ${active ? 'border-orange-200 text-slate-700 hover:border-orange-300 hover:bg-orange-50/40' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
        >
          <span className="flex items-center gap-2 min-w-0">
            <CalendarClock size={17} className={`flex-shrink-0 ${active ? 'text-orange-500' : 'text-slate-400'}`} strokeWidth={1.9} />
            <span className="truncate">Late checkouts</span>
          </span>
          <span className="flex items-center gap-1.5 flex-shrink-0">
            <span className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-xs font-bold ${active ? 'bg-gradient-to-r from-red-500 to-orange-400 text-white' : 'bg-slate-200 text-slate-500'}`}>
              {count}
            </span>
            <ChevronDown size={15} className="text-slate-400" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-72 p-0 rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-xl"
        data-testid={`${testid}-content`}
      >
        <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-red-50 to-orange-50">
          <h4 className="text-sm font-bold text-slate-900">Late checkouts</h4>
          <p className="text-xs text-slate-400">{count} scheduled for this day</p>
        </div>
        {count === 0 ? (
          <div className="px-4 py-6 text-center">
            <CalendarClock size={28} className="mx-auto text-slate-300 mb-2" strokeWidth={1.5} />
            <p className="text-sm text-slate-400">No late checkouts</p>
          </div>
        ) : (
          <div className="max-h-64 overflow-y-auto py-1">
            {items.map((lc) => (
              <div key={lc.id} className="flex items-center justify-between gap-2 px-4 py-2.5 hover:bg-slate-50 transition-colors" data-testid={`late-checkout-item-${lc.id}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0">
                    <Clock size={15} className="text-orange-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{lc.propertyName}</p>
                    <p className="text-xs text-slate-500 truncate">
                      Checkout at <span className="font-bold text-orange-600">{lc.time}</span>{lc.note ? ` · ${lc.note}` : ''}
                    </p>
                  </div>
                </div>
                {(canEdit || canDelete) && (
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    {canEdit && (
                      <button
                        onClick={() => onEdit?.(lc)}
                        className="w-7 h-7 rounded-lg hover:bg-orange-50 flex items-center justify-center group"
                        data-testid={`late-checkout-edit-${lc.id}`}
                        aria-label="Edit late checkout"
                      >
                        <Pencil size={14} className="text-slate-400 group-hover:text-orange-500" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => onDelete?.(lc.id)}
                        className="w-7 h-7 rounded-lg hover:bg-red-50 flex items-center justify-center group"
                        data-testid={`late-checkout-delete-${lc.id}`}
                        aria-label="Remove late checkout"
                      >
                        <Trash2 size={14} className="text-slate-400 group-hover:text-red-500" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
